import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { EditMidForm, CommissionType, CommissionValueType, ProcessorData, EditMerchantMid } from './edit-mid.types';
import AppFormInput from '../../../../components/form/app-form-input';
import { UserState } from '../../../../store/user.slice';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiService } from '../../../../services/api.service';
import { AppStore } from '../../../../store/store';
import AppFormSelect from '../../../../components/form/app-form-select';
import EditMidPartFormCommissionField from './edit-mid.part.form.commission-field';
import Button from '../../../../components/button';
import { useNavigate } from 'react-router-dom';
import './edit-mid.part.form.scss';
import { errorService } from '../../../../services/error.service';
import { CommissionApiResponse, MerchantMid } from '../../../../services/api.service-types';

const EditMidPartForm = ({ id }: { id: string }) => {
    const navigate = useNavigate();

    const user = useSelector<AppStore, UserState>(state => state.user);

    const [initialMid, setInitialMid] = useState<any>();

    const [processors, setProcessors] = useState<ProcessorData[]>();

    const form = useForm<EditMidForm>({
        defaultValues: {
            processorId: '',
            midName: '',
            midCode: '',
            commissions: Object.values(CommissionType).map((type) => ({
                valueType: CommissionValueType.PERCENT,
                type,
                anyValue: false,
            })),
        },
    });


    const {
        fields: commissionFields,
    } = useFieldArray({
        control: form.control,
        name: `commissions` as const,
    });

    useEffect(() => {
        apiService
            .getMidById(id)
            .then((response) => {
                const mappedData: EditMidForm = {
                    processorId: response.data.mid.processorId,
                    midName: response.data.mid.midName,
                    midCode: response.data.mid.midCode,
                    midDescriptor: response.data.mid.midDescriptor,
                    trashHoldLimit: response.data.mid.trashHoldLimit,
                    commissions: Object.values(CommissionType).map((type) => {
                        const existingCommission = response.data.commissions?.find(
                            (commission: any) => commission.commissionType === type
                        );
                        return {
                            value: existingCommission?.commissionAmount || 0,
                            valueType: existingCommission?.isPercent
                                ? CommissionValueType.PERCENT
                                : CommissionValueType.FIXED,
                            type,
                        };
                    }),
                    valid: response.data.mid.valid,
                };
                setInitialMid(response.data)
                form.reset(mappedData);
            })
            .catch((e) => console.error(e));
    }, [id, user]);

    useEffect(() => {
        const getProcessors = async () => {
            try {
                const response = await apiService.getAllProcessors();
                return response.data;
            } catch (e) {
                return []
            }
        }
        getProcessors().then((data) => {
            setProcessors(data)
        })
    }, [user])

    const processorsOptions = processors && processors?.map((processor) => {
        return {
            label: `${processor.processorName} (${processor.processorType})`,
            value: processor.id
        }
    })

    const handleUpdate = async () => {
        if (!initialMid) return;

        const formData = form.getValues();

        const updatedMid: EditMerchantMid = {
            mid: {
                id: initialMid.mid.id,
                processorId: formData.processorId,
                merchantId: initialMid.mid.merchantId,
                midCode: formData.midCode,
                midDescriptor: formData.midDescriptor,
                valid: formData.valid,
                trashHoldLimit: Number(formData.trashHoldLimit ?? 0),
                midName: formData.midName,
            },
            commissions: formData.commissions!.map((commission) => {
                const existing = initialMid.commissions?.find(
                    (c: CommissionApiResponse) => c.commissionType === commission.type
                );
                return {
                    id: existing?.id ?? null,
                    midId: initialMid.mid.id ?? null,
                    commissionType: commission.type,
                    commissionAmount: Number(commission.value),
                    isPercent: commission.valueType === CommissionValueType.PERCENT,
                    isFixed: commission.valueType === CommissionValueType.FIXED,
                } as Partial<CommissionApiResponse>;
            }),
        };


        try {
            await apiService.updateMid(updatedMid);
            errorService.showSuccess('Success', `MID ${formData.midCode} updated successfully`);
            navigate(-1);
        } catch (e) {
            errorService.showApiError(e);
        }
    };


    return (
        <div className="edit-mid-part-form flex-column flex-gap-30">
            <div className='flex-row flex-gap-20'>
                <AppFormInput
                    control={form.control}
                    name="midName"
                    placeholder="MID name"
                    validations={{ required: true }}
                />
                <AppFormInput
                    control={form.control}
                    name="trashHoldLimit"
                    type="number"
                    placeholder="Cap MID - $"
                    validations={{ required: true }}
                    hasCurrency={true}
                />
                <AppFormSelect
                    control={form.control}
                    name="processorId"
                    options={processorsOptions || []}
                    placeholder="Select Processor"
                    validations={{ required: true }}
                />
            </div>
            <div className="flex-row flex-gap-20">
                <AppFormInput
                    control={form.control}
                    name="midCode"
                    placeholder="Account Code"
                    validations={{ required: true }}
                />
                <AppFormInput
                    control={form.control}
                    name="midDescriptor"
                    placeholder="MID Descriptor"
                />
               
            </div>
            <div className="flex-row flex-gap-20 flex-wrap">
                {commissionFields.map((field, index) => (
                    <EditMidPartFormCommissionField
                        key={field.id}
                        form={form}
                        label={`Transaction Commission`}
                        secondaryLabel={field.type}
                        commissionName={`commissions.${index}` as const}
                    />
                ))}
            </div>
            <div className='flex-row flex-align-end-end flex-gap-40'>
                <Button
                    label='Cancel'
                    theme='secondary'
                    onClick={() => navigate(-1)}
                />
                <Button
                    label='Save'
                    disabled={!form.formState.isValid}
                    onClick={form.handleSubmit(handleUpdate)}
                />
            </div>
        </div>
    )
}

export default EditMidPartForm;